import React, { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { detectQueryLanguage } from "../utils/searchLogger";

interface TrendingTerm {
  query: string;
  count: number;
}

interface TrendingData {
  daily: TrendingTerm[];
  monthly: TrendingTerm[];
}

const DEFAULT_TERMS_BY_LANG: Record<string, TrendingTerm[]> = {
  en: [
    "crypto", "news", "trading", "movies", "music", "technology",
    "education", "games", "sports", "travel", "finance", "programming",
    "AI", "health", "food", "anime", "books", "business", "marketing", "science",
  ].map((q) => ({ query: q, count: 0 })),
  fa: [
    "تلگرام", "فیلم", "موسیقی", "اخبار", "آموزش", "بازی",
    "ورزش", "سفر", "کتاب", "تکنولوژی",
  ].map((q) => ({ query: q, count: 0 })),
  zh: [
    "新闻", "电影", "音乐", "游戏", "科技", "教育",
    "旅行", "金融", "美食", "健康",
  ].map((q) => ({ query: q, count: 0 })),
};

/**
 * Detect user's language from browser settings.
 * Maps to one of the supported languages: en, fa, zh.
 */
function getUserLanguage(): string {
  const lang = (navigator.language || "en").toLowerCase();
  if (lang.startsWith("fa") || lang.startsWith("ar")) return "fa";
  if (lang.startsWith("zh")) return "zh";
  return "en";
}

function getCacheKey(lang: string) {
  return `trendingSearches_v3_${lang}`;
}

const CACHE_TTL = 30 * 60 * 1000; // 30 minutes

function getCached(lang: string): TrendingData | null {
  try {
    const raw = localStorage.getItem(getCacheKey(lang));
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL) return null;
    return data;
  } catch {
    return null;
  }
}

function setCache(lang: string, data: TrendingData) {
  localStorage.setItem(getCacheKey(lang), JSON.stringify({ data, ts: Date.now() }));
}

/**
 * Read trending searches directly from Firestore.
 * Tries per-language docs first (daily_en), falls back to combined docs
 * and filters by language using script detection.
 */
async function fetchTrending(lang: string): Promise<TrendingData> {
  // Try per-language docs first
  const [dailyLangSnap, monthlyLangSnap] = await Promise.all([
    getDoc(doc(db, "trendingSearches", `daily_${lang}`)),
    getDoc(doc(db, "trendingSearches", `monthly_${lang}`)),
  ]);

  const daily = dailyLangSnap.exists() ? (dailyLangSnap.data()?.terms || []) : [];
  const monthly = monthlyLangSnap.exists() ? (monthlyLangSnap.data()?.terms || []) : [];

  // If per-language docs have data, use them
  if (daily.length > 0 || monthly.length > 0) {
    return { daily, monthly };
  }

  // Fallback: read combined docs and filter by language
  const [dailySnap, monthlySnap] = await Promise.all([
    getDoc(doc(db, "trendingSearches", "daily")),
    getDoc(doc(db, "trendingSearches", "monthly")),
  ]);

  const filterByLang = (terms: TrendingTerm[]) =>
    terms.filter((t) => detectQueryLanguage(t.query) === lang);

  return {
    daily: dailySnap.exists() ? filterByLang(dailySnap.data()?.terms || []) : [],
    monthly: monthlySnap.exists() ? filterByLang(monthlySnap.data()?.terms || []) : [],
  };
}

const TrendingSearches: React.FC = () => {
  const [data, setData] = useState<TrendingData | null>(null);
  const [tab, setTab] = useState<"daily" | "monthly">("daily");
  const lang = getUserLanguage();

  useEffect(() => {
    const cached = getCached(lang);
    if (cached) {
      setData(cached);
      return;
    }

    fetchTrending(lang)
      .then((d) => {
        setData(d);
        setCache(lang, d);
      })
      .catch((err) => {
        console.warn("Failed to fetch trending searches:", err);
      });
  }, [lang]);

  const defaultTerms = DEFAULT_TERMS_BY_LANG[lang] || DEFAULT_TERMS_BY_LANG.en;
  const terms =
    data && data[tab]?.length > 0 ? data[tab] : defaultTerms;

  const handleClick = (query: string) => {
    window.location.hash = `gsc.tab=0&gsc.q=${encodeURIComponent(query)}`;
  };

  return (
    <div style={styles.container}>
      <div style={styles.tabs}>
        <button
          style={tab === "daily" ? styles.tabActive : styles.tab}
          onClick={() => setTab("daily")}
        >
          🔥 Trending (24h)
        </button>
        <button
          style={tab === "monthly" ? styles.tabActive : styles.tab}
          onClick={() => setTab("monthly")}
        >
          📈 Popular (30d)
        </button>
      </div>
      <div style={styles.tagsContainer}>
        {terms.map((t, i) => (
          <button
            key={i}
            style={styles.tag}
            onClick={() => handleClick(t.query)}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,136,204,0.4)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            {t.query}
            {t.count > 0 && (
              <span style={styles.count}>{t.count}</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    background: "rgba(255,255,255,0.05)",
    borderRadius: "16px",
    padding: "1.25rem",
    marginTop: "1.5rem",
    maxWidth: "800px",
    width: "100%",
  },
  tabs: {
    display: "flex",
    gap: "0.5rem",
    marginBottom: "1rem",
  },
  tab: {
    background: "transparent",
    border: "1px solid rgba(255,255,255,0.15)",
    borderRadius: "20px",
    padding: "0.4rem 1rem",
    color: "rgba(255,255,255,0.6)",
    cursor: "pointer",
    fontSize: "0.85rem",
    fontWeight: 500,
    transition: "all 0.2s",
  },
  tabActive: {
    background: "rgba(0,136,204,0.2)",
    border: "1px solid #0088cc",
    borderRadius: "20px",
    padding: "0.4rem 1rem",
    color: "#fff",
    cursor: "pointer",
    fontSize: "0.85rem",
    fontWeight: 600,
    transition: "all 0.2s",
  },
  tagsContainer: {
    display: "flex",
    flexWrap: "wrap" as const,
    gap: "0.5rem",
  },
  tag: {
    background: "linear-gradient(135deg, #0088cc 0%, #0066aa 100%)",
    border: "none",
    borderRadius: "20px",
    padding: "0.4rem 0.85rem",
    color: "#fff",
    fontSize: "0.8rem",
    fontWeight: 500,
    cursor: "pointer",
    transition: "all 0.2s ease",
    display: "inline-flex",
    alignItems: "center",
    gap: "0.35rem",
  },
  count: {
    background: "rgba(255,255,255,0.2)",
    borderRadius: "10px",
    padding: "0.1rem 0.4rem",
    fontSize: "0.7rem",
  },
};

export default TrendingSearches;
